import { can } from '@pl/domain';
import type { ReasonCode, TransferDetailDto } from '@pl/domain';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ApiError, api } from '../../lib/api';
import { qk, useMe, useSiteMap, useSkuMap, useTransfer } from '../../lib/queries';
import { Bar, Empty, ErrorNote, RefetchNote, SkuTag } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { ScannerView } from '../scanner/ScannerView';
import { useScanSite } from '../useScanSite';
import { kit, plural, RevealedResult, ScanRedScreen, ScanSheet, waitingFor } from './scanKit';
import { BACK_TO_TRANSFERS, gateTransfer, RouteTags, useTransferScan } from './transferKit';
import s from './TransferIn.module.css';

const RED_TITLE: Partial<Record<ReasonCode, string>> = {
  NOT_IN_TRANSFER: 'Not on this transfer',
  PALLET_NOT_ALLOWED: 'Scan boxes, not pallets',
  ALREADY_SHIPPED: 'Already shipped',
  OTHER_SITE: 'Wrong site',
  WRITTEN_OFF: 'Written off',
  VOIDED: 'Voided sticker',
  UNKNOWN_CODE: 'Unknown sticker',
  INVALID_CODE: 'Unknown sticker',
};

/** A box that is not on the truck's paperwork is a lead's problem, not something to put on a shelf. */
const SET_ASIDE = 'Nothing was changed. Set this box aside and tell a lead.';

/**
 * Scanning a transfer's boxes into stock at the destination, then closing it.
 *
 * Closing is the only way the missing boxes become anyone's job: each one still in transit turns
 * into an open discrepancy here. So it is a lead's call when anything is missing (§6.6), and it is
 * locked while a scan is still on this phone — closing under that scan would call a box missing
 * that is standing on this dock.
 *
 * A closed transfer still takes boxes: one that turns up late is scanned in and closes its own
 * discrepancy.
 */
export function TransferIn() {
  const { transferId = '' } = useParams();
  const me = useMe();
  const { site } = useScanSite();
  const qc = useQueryClient();
  const query = useTransfer(transferId);
  const skuById = useSkuMap();
  const siteById = useSiteMap();

  const t = query.data;
  const scan = useTransferScan(transferId, t?.code, {
    mode: 'transfer_in',
    refused: 'Not received',
    locks: 'Close',
    refusalNote: SET_ASIDE,
    titles: RED_TITLE,
    fallbackTitle: "Can't receive this box",
    accepted: (response, code) => ({ tone: 'ok', title: response.message, message: `Into stock at ${site?.name ?? 'this site'}.`, code }),
  });
  const { pending, red, setRed } = scan;

  const [closeOpen, setCloseOpen] = useState(false);
  const keepScanning = useRef<HTMLButtonElement>(null);

  /** A close whose answer was lost is retried into a 409; a transfer that really is closed is the answer. */
  const close = useMutation({
    mutationFn: async () => {
      try {
        return await api.post<TransferDetailDto>(`/transfers/${transferId}/close`);
      } catch (err) {
        if (!(err instanceof ApiError) || err.code !== 'TRANSFER_NOT_IN_TRANSIT') throw err;
        const now = await api.get<TransferDetailDto>(`/transfers/${transferId}`).catch(() => null);
        if (now?.status !== 'closed') throw err;
        return now;
      }
    },
    onSuccess: (dto) => {
      setCloseOpen(false);
      qc.setQueryData(qk.transfer(transferId), dto);
      void qc.invalidateQueries({ queryKey: ['transfers'] });
    },
  });

  const gate = gateTransfer(site, query);
  if (gate || !site || !t) return gate;

  const from = siteById.get(t.fromSiteId);
  const to = siteById.get(t.toSiteId);

  if (t.toSiteId !== site.id) {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <Empty title={`${t.code} arrives at ${to?.name ?? 'another site'}`} description="Switch this phone to that site to scan it in." />
      </div>
    );
  }
  if (t.status === 'draft' || t.status === 'cancelled') {
    return (
      <div className={`scan-body ${kit.tkCentre}`}>
        <Empty
          title={t.status === 'draft' ? `${t.code} has not been sent yet` : `${t.code} was canceled`}
          description={t.status === 'draft' ? `${from?.name ?? 'The other site'} is still scanning it.` : 'Nothing is on its way.'}
          action={
            <Link to="/scan/transfer" className="bigbtn ghost">
              Back to transfers
            </Link>
          }
        />
      </div>
    );
  }

  const { sent, received, stillInTransit: missing } = t.totals;
  const closed = t.status === 'closed';
  const lead = Boolean(me.data && can(me.data.role, 'close_transfer_missing'));
  const leadOnly = missing > 0 && !lead;
  const closeLocked = pending > 0 || leadOnly;
  const scanning = !closed || missing > 0;
  const lines = t.lines.filter((l) => l.sent > 0);
  const lockNotes = [
    missing > 0 ? (leadOnly ? `Lead only · ${plural(missing, 'box', 'boxes')} not scanned yet` : `${plural(missing, 'box', 'boxes')} not scanned yet`) : null,
    pending > 0 ? waitingFor(pending) : null,
  ].filter((x): x is string => x !== null);

  return (
    <>
      <div className={`scan-body pt ${s.inBody}`}>
        <div className={s.inHead}>
          <div className={s.inIdent}>
            <div className={`mono ${s.inCode}`}>{t.code}</div>
            <div className={s.inFrom}>{`From ${from?.name ?? 'another site'}`}</div>
          </div>
          <RouteTags from={from} to={to} />
        </div>
        <RefetchNote queries={[query]} />

        <div className={s.progress}>
          <div className={s.progTop}>
            <b>{received}</b>
            <span>{`of ${sent} received`}</span>
            {closed && (
              <span className={`chip ${missing > 0 ? 'warn' : 'ok'} ${s.closedChip}`}>{missing > 0 ? `Closed · ${missing} missing` : 'Closed · all in'}</span>
            )}
          </div>
          <Bar value={received} max={sent} tone={received >= sent ? 'ok' : 'accent'} />
          <ul className={s.skuList} aria-label="Received by SKU">
            {lines.map((l) => (
              <li key={l.skuId} className={s.skuRow}>
                <span className={`skux ${s.skuCell}`}>
                  <SkuTag sku={skuById.get(l.skuId)} short />
                </span>
                <span className={s.skuTally}>
                  <span className={`mono ${s.skuCount}`}>
                    {l.received}/{l.sent}
                  </span>
                  <Bar value={l.received} max={l.sent} tone={l.received >= l.sent ? 'ok' : undefined} />
                </span>
              </li>
            ))}
          </ul>
        </div>

        {scanning ? (
          <>
            {/* Stopped while a red outcome holds the screen, so the next sticker cannot slip past
                unread and a wedge scanner's Enter cannot double as the dismissal. */}
            {/* Also stopped while Close is on its way: a box read then lands on a closing transfer. */}
            <ScannerView onCode={scan.handleCode} enabled={red === null && !close.isPending} />
            <RevealedResult result={scan.shownResult} revealOn={scan.result} />
            {closed && <p className={s.lateNote}>{`${plural(missing, 'box is', 'boxes are')} still missing. Scan ${missing === 1 ? 'it' : 'them'} in here if ${missing === 1 ? 'it turns' : 'they turn'} up.`}</p>}
          </>
        ) : (
          <p className={s.lateNote}>Every box on this transfer is accounted for.</p>
        )}
      </div>

      {!closed && (
        <div className="scan-foot">
          {lockNotes.length > 0 && (
            <p className={kit.tkFootNote}>
              {lockNotes.map((note) => (
                <span key={note}>{note}</span>
              ))}
            </p>
          )}
          <button type="button" className={`bigbtn ghost${closeLocked ? ' locked' : ''}`} disabled={closeLocked || close.isPending} onClick={() => setCloseOpen(true)}>
            {closeLocked && <Icon name="lock" />}
            Close transfer…
          </button>
        </div>
      )}

      <ScanSheet
        open={closeOpen}
        onClose={() => {
          setCloseOpen(false);
          close.reset();
        }}
        busy={close.isPending}
        safeFocus={keepScanning}
        title={missing > 0 ? `Close with ${missing} missing?` : `Close ${t.code}?`}
        lead={
          missing > 0
            ? `Each missing box becomes a discrepancy. ${missing === 1 ? 'It' : 'They'} can still be scanned in here if ${missing === 1 ? 'it turns' : 'they turn'} up.`
            : `All ${plural(sent, 'box is', 'boxes are')} in at ${site.name}.`
        }
        actions={
          <>
            <ErrorNote error={close.error} />
            <button type="button" className={`bigbtn${missing > 0 ? ` ${kit.tkDanger}` : ''}`} disabled={close.isPending || closeLocked} onClick={() => close.mutate()}>
              {missing > 0 ? `Close with ${missing} missing` : 'Close transfer'}
            </button>
            <button type="button" ref={keepScanning} className="bigbtn ghost" disabled={close.isPending} onClick={() => setCloseOpen(false)}>
              Keep scanning
            </button>
          </>
        }
      />

      <ScanRedScreen back={BACK_TO_TRANSFERS} red={red} resumeLabel="Got it, set it aside" onDismiss={() => setRed(null)} />
    </>
  );
}
